var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var NodeElement_1;
import { ToolType } from '@symon-ai/wisepipe-base-pipe';
import { JSON, JsonDiscriminatorValue, JsonSchema } from '@symon-ai/wisepipe-serialize';
import { CloudConfig } from './cloudConfig';
import { DistinctConfig } from './distinctConfig';
import { ExportConfig } from './exportConfig';
import { GraphElement } from './graphElement';
import { PartialNodeElement } from './partialNodeElement';
import { RemoveEmptyConfig } from './removeEmptyConfig';
import { SampleCustomersConfig } from './sampleCustomersConfig';
import { SnapshotConfig } from './snapshotConfig';
// eslint-disable-next-line import/no-duplicates
import './toolConfig'; // force import for JsonSchema
// eslint-disable-next-line import/no-duplicates
import { MultiUnionConfig } from './multiUnionConfig';
import { getToolInfo } from './toolConfigHelper';
import { UnionConfig } from './unionConfig';
import { ReusableLogicOutputConfig } from './reusableLogicOutputConfig';
// Data for nodes within RLs that are calced will be stored at different S3 paths (based on their location within RL nodes)
export const getElementIDForS3 = (elementID, nodeIDPathFromReusableLogic) => {
    return nodeIDPathFromReusableLogic ?? elementID;
};
export const getElementIDForS3FromNode = (node) => {
    return getElementIDForS3(node.elementID, node.nodeIDPathFromReusableLogic);
};
const schema = {
    $id: 'urn:wisepipe-common-pipe:NodeElement',
    type: 'object',
    properties: {
        x: { type: 'number', minimum: -100000, maximum: 100000 },
        y: { type: 'number', minimum: -100000, maximum: 100000 },
        nameOverride: { type: 'string' },
        color: { type: 'string', minLength: 7, maxLength: 7 },
        parentID: { oneOf: [{ type: 'string', uid: 10 }, { type: 'null' }] },
        nodeIDPathFromReusableLogic: { type: 'string' }
    },
    if: {
        required: ['config']
    },
    then: {
        properties: {
            config: { $ref: 'urn:wisepipe-common-pipe:ToolConfig' },
            toolType: { type: 'string', const: { $data: '1/config/toolType' } }
        }
    },
    else: {
        properties: {
            toolType: {
                type: 'string',
                // build a list of valid tooltypes, except ToolType.NONE
                enum: Object.keys(ToolType)
                    .map((key) => ToolType[key])
                    .filter((key) => typeof key !== 'function' && key !== ToolType.NONE)
            }
        }
    },
    required: ['x', 'y', 'toolType']
};
let NodeElement = NodeElement_1 = class NodeElement extends GraphElement {
    constructor(elementID, toolType, x, y, config, lastModified, nameOverride, color, uniqueChangeID, parentID, nodeIDPathFromReusableLogic) {
        super(elementID, 'node', lastModified, uniqueChangeID);
        this.toolType = toolType;
        this.x = x;
        this.y = y;
        const actual = config || this.getDefaultConfig();
        if (actual)
            this.config = actual;
        if (nameOverride)
            this.nameOverride = nameOverride;
        if (color)
            this.color = color;
        this.parentID = parentID;
        this.nodeIDPathFromReusableLogic = nodeIDPathFromReusableLogic;
        JSON.validate(this, NodeElement_1);
    }
    getDefaultConfig() {
        return NodeElement_1.getDefaultConfig(this.toolType);
    }
    static getDefaultConfig(toolType) {
        switch (toolType) {
            case ToolType.CLOUD:
                return new CloudConfig();
            case ToolType.SAMPLE_CUSTOMERS:
                return new SampleCustomersConfig();
            case ToolType.UNION:
                return new UnionConfig();
            case ToolType.MULTI_UNION:
                return new MultiUnionConfig();
            case ToolType.REMOVE_EMPTY:
                return new RemoveEmptyConfig([]);
            case ToolType.SNAPSHOT:
                return new SnapshotConfig();
            case ToolType.EXPORT:
                return new ExportConfig();
            case ToolType.DISTINCT:
                return new DistinctConfig([]);
            case ToolType.REUSABLE_LOGIC_OUTPUT:
                return new ReusableLogicOutputConfig();
            default:
                return undefined;
        }
    }
    getName() {
        return NodeElement_1.getName(this.toolType, this.nameOverride);
    }
    static getName(toolType, nameOverride) {
        switch (toolType) {
            case ToolType.FILE:
                return nameOverride ?? undefined;
            default:
                return nameOverride ?? getToolInfo(toolType).displayName;
        }
    }
    getToolNameForAuditLog() {
        return PartialNodeElement.getToolNameForAuditLog(this.getName(), this.toolType);
    }
    static getNodeElementsByType(elements, toolType) {
        return elements.filter((e) => e.elementType === 'node' && e.toolType === toolType);
    }
    applyParameters(parameters) {
        return new NodeElement_1(this.elementID, this.toolType, this.x, this.y, this.config?.applyParameters(parameters), this.lastModified, this.nameOverride, this.color, this.uniqueChangeID, this.parentID, this.nodeIDPathFromReusableLogic);
    }
};
NodeElement = NodeElement_1 = __decorate([
    JsonSchema(schema),
    JsonDiscriminatorValue('node')
], NodeElement);
export { NodeElement };
//# sourceMappingURL=nodeElement.js.map